import type { Instance } from "@gadgetinc/mobx-quick-tree";
import type { DeepReadonly } from "ts-essentials";
import type { ModelField } from "../database/ModelField";
import type { LiveOrReadOnlyServerRoot } from "../LiveOrServerRoot";
import { FieldType } from "../type-system/FieldType";
import { getGeneratingRoot } from "../utils";
import { ShopifyShopKey } from "./shopify/keys";
import type { ShopifyApiVersion } from "./shopify/ShopifyApiVersion";
import { ShopifyDominion } from "./shopify/ShopifyDominion";
import { getRootWebhookModelKey, upsertWebhookTopicsForModelKey } from "./shopify/ShopifyModelMetadata";
import type { ShopifyWebhookTopic } from "./shopify/webhooks";
import type { ShopifyConnectionConfig } from "./ShopifyConnectionConfig";
import type { ConnectionModelFieldSpec } from "./utils";

type FieldLike =
  | ModelField
  | { modelKey: string; apiVersion: ShopifyApiVersion; fieldSpec: ConnectionModelFieldSpec | DeepReadonly<ConnectionModelFieldSpec> };

/**
 * Uses the legacy way of determining if a field is a sync-only field when the model was still syncing via REST sync config
 * @param field - The ModelField to check
 * @returns True if the field is a legacy sync-only field, false otherwise
 */
const isLegacyShopifySyncOnlyField = (field: ModelField | DeepReadonly<ConnectionModelFieldSpec>) => {
  return !!field.contributorConfiguration?.[ShopifyDominion.id]?.graphQLOnlyField;
};

const isBelongsToShopModelField = (field: ModelField | DeepReadonly<ConnectionModelFieldSpec>) => {
  return field.configuration?.relatedModelKey === ShopifyShopKey;
};

export const isShopifyWebhookField = (field: FieldLike) => {
  const fieldType = "fieldSpec" in field ? field.fieldSpec.fieldType : field.fieldType;

  if (fieldType == FieldType.ID) return false;

  const contributorConfiguration = "fieldSpec" in field ? field.fieldSpec.contributorConfiguration : field.contributorConfiguration;
  const shopifyDominionConfig = contributorConfiguration?.[ShopifyDominion.id];

  if (!shopifyDominionConfig) return false;

  let availableInWebhooks: readonly string[] | null | undefined;
  let modelKey: string;
  let apiVersion: ShopifyApiVersion;

  if ("fieldSpec" in field) {
    modelKey = field.modelKey;
    availableInWebhooks = field.fieldSpec.contributorConfiguration?.[ShopifyDominion.id]?.availableInWebhook;
    apiVersion = field.apiVersion;
  } else {
    const root: LiveOrReadOnlyServerRoot = getGeneratingRoot(field);
    const shopifyConnection = root.maybeGetShopifyConnection();

    if (!shopifyConnection) return false;
    apiVersion = (shopifyConnection.configuration as Instance<typeof ShopifyConnectionConfig>).shopifyApiVersion;
    modelKey = field.model.key;
    availableInWebhooks = field.contributorConfiguration?.[ShopifyDominion.id]?.availableInWebhook;
  }
  if (!availableInWebhooks) return false;

  const modelWebhooks = upsertWebhookTopicsForModelKey(modelKey, apiVersion);

  return availableInWebhooks.some((topic) => modelWebhooks.includes(topic as ShopifyWebhookTopic));
};

/**
 * Checks if a field is a standardized sync-only field based on the expected sync only field configuration post GraphQL migration, where there is a graphQLFieldKey and no shopifyFieldKey in the field configuration
 * @param field - The ModelField to check
 * @returns True if the field is a standardized sync-only field, false otherwise
 */
const isStandardizedSyncOnlyField = (field: ModelField | DeepReadonly<ConnectionModelFieldSpec>) => {
  const shopifyDominionConfig = field.contributorConfiguration?.[ShopifyDominion.id];

  if (!shopifyDominionConfig) return false;

  const hasGraphQLFieldKey = !!shopifyDominionConfig.graphQLFieldKey;
  const hasShopifyFieldKey = !!shopifyDominionConfig.shopifyFieldKey;

  return hasGraphQLFieldKey && !hasShopifyFieldKey;
};

export const isBelongsToParentShopifyWebhookModelField = (field: ModelField) => {
  if (field.fieldType !== FieldType.BelongsTo) return false;

  const parentModelKey = getRootWebhookModelKey(field.model.key);

  return field.configuration.relatedModelKey === parentModelKey;
};

export const isShopifySyncOnlyField = (field: FieldLike) => {
  const f = "fieldSpec" in field ? field.fieldSpec : field;
  const shopifyDominionConfig = f.contributorConfiguration?.[ShopifyDominion.id];

  if (!shopifyDominionConfig) return false;

  // We always set the Shop field
  if (isBelongsToShopModelField(f)) return false;

  if (isLegacyShopifySyncOnlyField(f)) {
    return true;
  }

  if (isShopifyWebhookField(field)) {
    return false;
  }

  return isStandardizedSyncOnlyField(f);
};
